<script setup lang="ts">
/**
 * Dynamic block properties of the selected block (flip states, visibility states,
 * preset sizes, point parameters) — the equivalent of AutoCAD's "Custom" section in
 * its Properties palette.
 *
 * Every editable parameter is listed, those with a canvas grip included: each row
 * carries the glyph of its grip, so a square on the drawing can always be traced
 * back to a named parameter here.
 *
 * Property names and values come straight from the customer's DWG, so they are shown
 * as authored and never translated.
 */
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Facade } from "@/drawing";
import type {
  DynBlockPanelRow,
  DynBlockPropertyInfo,
} from "@/drawing/blocks/dynamicGrouping";
import { Logger } from "@/logger";
import CanvasButton from "../CanvasButton.vue";
import FloatingPanel from "../FloatingPanel.vue";
import BlockBadgeIcon from "../BlockBadgeIcon.vue";
import DynBlockRowGlyph from "../DynBlockRowGlyph.vue";
import DynBlockValueField from "./DynBlockValueField.vue";

const props = defineProps<{
  target: string;
}>();

const rows = ref<DynBlockPanelRow[]>([]);
const isOpen = ref(false);

function refresh(): void {
  if (!Facade.isInitialized) {
    rows.value = [];
    return;
  }
  rows.value = Facade.dynBlocks.getSelectedPanelRows();
}

// The selection itself changes the target; undo/redo changes values without it.
watch(() => props.target, refresh, { immediate: true });

onMounted(() => {
  if (Facade.isInitialized) Facade.history.onChange(refresh);
});

onBeforeUnmount(() => {
  if (Facade.isInitialized) Facade.history.offChange(refresh);
});

function toggle(): void {
  isOpen.value = !isOpen.value;
  if (isOpen.value) refresh();
}

function setValue(property: DynBlockPropertyInfo, value: string): void {
  if (!Facade.isInitialized || value === property.value) return;
  Logger.info(`DynBlockPropertiesPanel : ${property.name} = ${value}`);
  Facade.dynBlocks.setSelectedProperty(property.name, value);
  refresh();
}

/** Live step of a continuous edit — applied, not recorded. */
function previewValue(property: DynBlockPropertyInfo, value: string): void {
  if (!Facade.isInitialized || value === property.value) return;
  Facade.dynBlocks.previewSelectedProperty(property.name, value);
  refresh();
}

/** End of a continuous edit: the whole gesture becomes one undo step. */
function commitValue(
  property: DynBlockPropertyInfo,
  oldValue: string,
  newValue: string,
): void {
  if (!Facade.isInitialized) return;
  Facade.dynBlocks.commitSelectedProperty(property.name, oldValue, newValue);
  refresh();
}
</script>

<template>
  <div>
    <CanvasButton
      class="canvas-btn-wide"
      :active="isOpen"
      :tooltipText="$t('canvas:floating.dynblock.tooltip.text')"
      :tooltipTitle="$t('canvas:floating.dynblock.tooltip.title')"
      @click="toggle"
    >
      <BlockBadgeIcon badge="lightning" badge-color="#ffc107" />
    </CanvasButton>

    <FloatingPanel
      panel-id="dyn-block-properties"
      :title="$t('canvas:floating.dynblock.header')"
      :open="isOpen"
      @close="isOpen = false"
    >
      <p v-if="!rows.length" class="empty-hint">
        {{ $t("canvas:floating.dynblock.empty") }}
      </p>

      <div v-for="row in rows" :key="row.key" class="field">
        <DynBlockRowGlyph :glyph="row.glyph" />
        <span
          class="field-label"
          :class="{ truncate: !!row.point }"
          :title="row.point ? row.label : undefined"
        >
          {{ row.label }}
        </span>

        <DynBlockValueField
          v-if="row.property"
          :property="row.property"
          @change="(value) => setValue(row.property!, value)"
          @preview="(value) => previewValue(row.property!, value)"
          @commit="(from, to) => commitValue(row.property!, from, to)"
        />

        <!-- Point parameter: both coordinates on the parameter's own line. -->
        <div v-else-if="row.point" class="field-point">
          <span class="axis">X</span>
          <DynBlockValueField
            compact
            :property="row.point.x"
            @change="(value) => setValue(row.point!.x, value)"
          />
          <span class="axis">Y</span>
          <DynBlockValueField
            compact
            :property="row.point.y"
            @change="(value) => setValue(row.point!.y, value)"
          />
        </div>
      </div>
    </FloatingPanel>
  </div>
</template>

<style lang="scss" scoped>
.empty-hint {
  margin: 0;
  padding: 0.25rem 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
}

.field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;

  & + .field {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.field-label {
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
  overflow-wrap: anywhere;

  // A point row spends most of its width on the two coordinate fields: wrapping
  // the name there would make the row two lines tall, so it is clipped instead
  // (in full in the title, and readable again by widening the panel).
  &.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.field-point {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.axis {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
}
</style>
